import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { GET_BASE_URL_IMAGE, dTime } from '../constants/apiEndpoints';
import { getMouseWaitUserAvatarUrl } from '../utils/avatar';
import { buildProfileImageUrl, getUploadedProfileImageName } from '../utils/profileImage';

type ThankAvatarStripProps = {
  thanks?: any[];
  resort?: 'disneyland' | 'disneyworld';
  maxVisible?: number;
};

const normalizeThankUser = (entry: any) => {
  const user = entry?.user ?? entry?.thankuser ?? entry?.userdetail ?? entry;

  return {
    ...user,
    user_id: user?.user_id ?? entry?.user_id,
    user_name: user?.user_name ?? entry?.user_name,
    image:
      user?.image ??
      entry?.image ??
      user?.user_image ??
      entry?.user_image ??
      user?.profile_image ??
      entry?.profile_image,
  };
};

export const getProfileImageCandidates = (user: any) => {
  const image = String(user?.image || '').trim();
  const userIdImage = getUploadedProfileImageName(user?.user_id);
  const candidates = [];

  if (image) {
    candidates.push(GET_BASE_URL_IMAGE + '/disneyland/images/thumbs/' + image + dTime);
    candidates.push(buildProfileImageUrl(image));
  }

  if (userIdImage && userIdImage !== image) {
    candidates.push(buildProfileImageUrl(userIdImage));
  }

  return Array.from(new Set(candidates));
};

const ThankAvatarImage = ({ user }: { user: any }) => {
  // Keyed on the values, not the user object, which is rebuilt every render.
  const imageCandidates = useMemo(
    () => getProfileImageCandidates({ image: user?.image, user_id: user?.user_id }),
    [user?.image, user?.user_id]
  );
  const [candidateIndex, setCandidateIndex] = useState(0);
  const fallbackUrl = imageCandidates[candidateIndex];

  if (!fallbackUrl) {
    return (
      <div className="mw-thank-avatar-fallback" aria-hidden="true">
        {String(user.user_name).charAt(0).toUpperCase()}
      </div>
    );
  }

  return (
    <img
      src={getMouseWaitUserAvatarUrl(user, fallbackUrl)}
      alt={`${user.user_name} avatar`}
      loading="lazy"
      decoding="async"
      onError={() => {
        setCandidateIndex((currentIndex) =>
          currentIndex + 1 < imageCandidates.length ? currentIndex + 1 : imageCandidates.length
        );
      }}
    />
  );
};

export const ThankAvatarStrip: React.FC<ThankAvatarStripProps> = ({
  thanks = [],
  resort = 'disneyland',
  maxVisible = 7,
}) => {
  const [expanded, setExpanded] = useState(false);
  const users = Array.isArray(thanks)
    ? thanks.map(normalizeThankUser).filter((user) => user?.user_id && user?.user_name)
    : [];

  if (users.length === 0) {
    return null;
  }

  const visibleUsers = expanded ? users : users.slice(0, maxVisible);
  const overflowCount = users.length - visibleUsers.length;
  const names = users.map((user) => user.user_name).join(', ');

  return (
    <div className="mw-thank-avatar-strip" aria-label={`Thanked by ${names}`}>
      {visibleUsers.map((user) => {
        const profileUrl = `/${resort}/user/${user.user_id}/mypost`;

        return (
          <Link
            to={profileUrl}
            className="mw-thank-avatar-link"
            title={user.user_name}
            aria-label={`${user.user_name} profile`}
            key={user.user_id}
          >
            <ThankAvatarImage user={user} />
          </Link>
        );
      })}
      {overflowCount > 0 && (
        <button
          type="button"
          className="mw-thank-avatar-overflow"
          aria-label={`Show ${overflowCount} more thank yous`}
          onClick={(event) => {
            event.preventDefault();
            event.stopPropagation();
            setExpanded(true);
          }}
        >
          +{overflowCount}
        </button>
      )}
    </div>
  );
};
